import React, { useState, useEffect } from "react";
import { useNavigation, useGetIdentity, useLogout } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import logo from "../../../logo.svg";
import { Layout, Menu, Drawer, Avatar, Dropdown, Typography } from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  BookOutlined,
  SettingOutlined,
  ReadOutlined,
  CheckSquareOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  MoreOutlined,
  TrophyOutlined,
  QuestionCircleOutlined,
  CalendarOutlined,
  FundProjectionScreenOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";

const { Sider } = Layout;
const { Text } = Typography;

const SidebarHeader: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { isDark } = useTheme();

  return (
    <div
      style={{
        padding: collapsed ? "16px 8px" : "16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        height: 64,
        borderBottom: `1px solid ${isDark ? "#27272a" : "hsl(var(--border))"}`,
        transition: "all 0.3s",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          background: "#FFC200",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <img src={logo} alt="DYEN" style={{ width: 20, height: "auto" }} />
      </div>
      {!collapsed && (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, overflow: "hidden" }}>
          <Text strong style={{ color: isDark ? "#fff" : "#000", fontSize: 14 }}>
            DYEN Tutor
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Workspace
          </Text>
        </div>
      )}
    </div>
  );
};

const SidebarFooter: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { data: user } = useGetIdentity();
  const { mutate: logout } = useLogout();
  const { push } = useNavigation();
  const { isDark } = useTheme();

  const handleLogout = () => {
    logout();
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
      onClick: () => push("/tutor/profile"),
    },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "Settings",
      onClick: () => push("/tutor/settings"),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log out",
      danger: true,
      onClick: handleLogout,
    },
  ];

  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    const storedUser = localStorage.getItem("userData") || localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        return `${parsed.firstName || ""} ${parsed.lastName || ""}`.trim() || "Tutor";
      } catch (e) {
        return "Tutor";
      }
    }
    return "Tutor";
  };

  const getUserEmail = () => {
    if (user?.email) return user.email;
    const storedUser = localStorage.getItem("userData") || localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        return parsed.email || "";
      } catch (e) {
        return "";
      }
    }
    return "";
  };

  return (
    <div
      style={{
        borderTop: `1px solid ${isDark ? "#27272a" : "hsl(var(--border))"}`,
        padding: collapsed ? "12px 8px" : "12px",
      }}
    >
      <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="topRight">
        <div
          className="sidebar-user-section sidebar-user-trigger"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px",
            borderRadius: 6,
            cursor: "pointer",
            transition: "background 0.2s",
          }}
        >
          <Avatar
            size={collapsed ? 32 : 36}
            style={{ backgroundColor: "#FFC200", color: "#000", flexShrink: 0 }}
            icon={<UserOutlined />}
          />

          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
              <Text strong ellipsis style={{ color: isDark ? "#fff" : "#000", fontSize: 13, lineHeight: 1.2 }}>
                {getUserDisplayName()}
              </Text>
              <Text type="secondary" ellipsis style={{ fontSize: 11, lineHeight: 1.2 }}>
                {getUserEmail()}
              </Text>
            </div>
          )}

          {!collapsed && <MoreOutlined style={{ color: "hsl(var(--muted-foreground))", fontSize: 16 }} />}
        </div>
      </Dropdown>
    </div>
  );
};

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (val: boolean) => void;
  mobileDrawerOpen?: boolean;
  setMobileDrawerOpen?: (val: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  setCollapsed,
  mobileDrawerOpen = false,
  setMobileDrawerOpen,
}) => {
  const { push } = useNavigation();
  const { isDark } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const next = window.innerWidth < 768;
      setIsMobile(next);
      if (!next && mobileDrawerOpen) {
        setMobileDrawerOpen?.(false);
      }
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [mobileDrawerOpen, setMobileDrawerOpen]);

  type MenuItem = Required<MenuProps>["items"][number];

  const getMenuItem = (
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: "group"
  ): MenuItem => ({ key, icon, children, label, type } as MenuItem);

  const items: MenuItem[] = [
    {
      type: "group",
      label: "Workspace",
      children: [getMenuItem("Dashboard", "dashboard", <DashboardOutlined />)],
    },
    {
      type: "group",
      label: "Students",
      children: [
        getMenuItem("Attendance", "attendance", <CheckSquareOutlined />),
        getMenuItem("Sessions", "attendance-sessions", <CalendarOutlined />),
        getMenuItem("Tasks", "tasks", <ReadOutlined />),
      ],
    },
    {
      type: "group",
      label: "Academics",
      children: [
        getMenuItem("Curriculum", "curriculum", <BookOutlined />),
        getMenuItem("Presentations", "presentations", <FundProjectionScreenOutlined />),
        getMenuItem("Grading", "grading", <TrophyOutlined />),
        getMenuItem("Quizzes", "quizzes", <QuestionCircleOutlined />),
      ],
    },
    {
      type: "group",
      label: "Support",
      children: [getMenuItem("Tickets", "tickets", <CustomerServiceOutlined />)],
    },
  ];

  const routeMap: Record<string, string> = {
    dashboard: "dashboard",
    attendance: "attendance",
    "attendance-sessions": "attendance/sessions",
    tasks: "tasks",
    curriculum: "curriculum",
    presentations: "presentations",
    grading: "grading",
    quizzes: "quizzes",
    tickets: "tickets",
    settings: "settings",
  };

  const handleClick: MenuProps["onClick"] = (e) => {
    const key = String(e.key);
    if (key === "profile" || key === "logout") return;

    const route = `/tutor/${routeMap[key] || key}`;
    push(route);
    if (isMobile) setMobileDrawerOpen?.(false);
  };

  const sidebarBg = isDark ? "#09090b" : "#FFFFFF";
  const borderColor = isDark ? "#27272a" : "#E4E4E5";

  const SidebarContent = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: sidebarBg }}>
      <SidebarHeader collapsed={collapsed && !isMobile} />

      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }} className="custom-scrollbar">
        <Menu
          theme={isDark ? "dark" : "light"}
          mode="inline"
          defaultSelectedKeys={["dashboard"]}
          items={items}
          onClick={handleClick}
          inlineIndent={16}
          style={{
            background: sidebarBg,
            borderRight: "none",
            fontSize: 13,
            fontWeight: 500,
          }}
          className="shadcn-menu"
        />
      </div>

      <SidebarFooter collapsed={collapsed && !isMobile} />
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        onClose={() => setMobileDrawerOpen?.(false)}
        open={mobileDrawerOpen}
        width={280}
        styles={{
          body: { padding: 0, background: sidebarBg },
          mask: { background: "rgba(0, 0, 0, 0.5)" },
        }}
        closeIcon={null}
      >
        {SidebarContent}
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      width={250}
      collapsedWidth={64}
      style={{
        background: sidebarBg,
        borderRight: `1px solid ${borderColor}`,
        height: "100vh",
        position: "sticky",
        top: 0,
        left: 0,
        zIndex: 100,
      }}
      trigger={null}
    >
      {SidebarContent}
    </Sider>
  );
};

export default Sidebar;
